import { Binding } from '@adalo/constants'
import BindingValue from 'components/Shared/BindingValue'
import { assetURL } from 'utils/assets'
import { isFirefox } from 'utils/browsers'
import { Zoom } from 'utils/canvasTypes'
import { normalizeColor } from 'utils/colors'
import { Branding } from 'utils/colorsTypes'
import { getFontFamily } from 'utils/type'

type IconButtonProps = {
  rendered: boolean
  iconRendered?: string | undefined
  iconSize: number
  iconFontSize: number
  iconColor: string
  zoom: Zoom
  branding: Branding
  id: string
  x: number
  sectionTop: number
  label?: string | [string | Binding] | undefined
  fontStyles?: {
    color: string
    fontSize: number
    fontWeight: number
    fontFamily: string
  }
}

type CustomIcon = {
  body: {
    url: string
    filename: string
  }
}

type IconRenderResult = {
  type: 'material' | 'custom'
  element: JSX.Element
  filterDef?: JSX.Element
}

const renderIcon = (
  icon: string | CustomIcon,
  iconColor: string,
  iconSize: number,
  id: string
): IconRenderResult => {
  if (typeof icon === 'string') {
    return {
      type: 'material',
      element: (
        <i
          className="material-icons"
          style={{
            color: iconColor,
            fontSize: iconSize,
            display: 'block',
          }}
        >
          {icon.replace(/-/g, '_')}
        </i>
      ),
    }
  }

  const { body } = icon
  const fullURL = assetURL(body.url)
  const filterId = `icon-color-filter-${id}-${body.filename.replace(
    /[^a-zA-Z0-9]/g,
    ''
  )}-${iconColor?.replace(/[^a-zA-Z0-9]/g, '')}`

  return {
    type: 'custom',
    filterDef: (
      <filter id={filterId} colorInterpolationFilters="sRGB">
        <feFlood floodColor={iconColor} result="flood" />
        <feComposite in="flood" in2="SourceAlpha" operator="in" result="mask" />
      </filter>
    ),
    element: (
      <image
        x={0}
        y={0}
        width={iconSize}
        height={iconSize}
        href={fullURL}
        preserveAspectRatio="xMidYMid meet"
        aria-label={body.filename}
        filter={`url(#${filterId})`}
      />
    ),
  }
}

export const IconButton = (props: IconButtonProps): JSX.Element => {
  const {
    rendered,
    iconRendered,
    iconSize,
    iconFontSize,
    iconColor,
    zoom,
    branding,
    id,
    x,
    sectionTop,
    label,
    fontStyles,
  } = props

  const {
    color = '@text',
    fontSize = 14,
    fontWeight = 400,
    fontFamily = 'inherit',
  } = fontStyles || {}

  if (!rendered) {
    return <></>
  }

  const iconResult = iconRendered
    ? renderIcon(iconRendered, iconColor, iconFontSize, id)
    : null

  return (
    <>
      {iconResult &&
        (iconResult.type === 'material' ? (
          <foreignObject
            x={x}
            y={sectionTop}
            height={iconSize}
            width={iconSize}
          >
            {iconResult.element}
          </foreignObject>
        ) : (
          <g transform={`translate(${x}, ${sectionTop})`}>
            <defs>{iconResult.filterDef}</defs>
            {iconResult.element}
          </g>
        ))}
      {label ? (
        <text
          x={0}
          y={0}
          fill={normalizeColor(color, branding)}
          fontSize={fontSize * zoom.scale}
          fontWeight={fontWeight}
          clipPath={`url(#bottom-caption-path-${id})`}
        >
          <tspan
            alignmentBaseline="text-before-edge"
            x={x + 12}
            y={sectionTop + 3.5 - fontSize / 4 + (isFirefox() ? 8 : 1)}
            style={{
              fontFamily: getFontFamily(fontFamily, branding),
            }}
          >
            <BindingValue binding={label} objectId={id} />
          </tspan>
        </text>
      ) : null}
    </>
  )
}
